import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Download, CheckCircle, Plus, IndianRupee } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  Draft: 'bg-gray-500/15 text-gray-400',
  Approved: 'bg-emerald-500/15 text-emerald-400',
  Exported: 'bg-blue-500/15 text-blue-400',
};

export default function BillingPage() {
  const [billings, setBillings] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [form, setForm] = useState({
    contract_id: '', billing_period_start: '', billing_period_end: '', tax_percent: '18',
  });

  const fetchData = async () => {
    try {
      const [bRes, cRes] = await Promise.all([
        api.get('/billing'),
        api.get('/contracts'),
      ]);
      setBillings(bRes.data);
      setContracts(Array.isArray(cRes.data) ? cRes.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleGenerate = async () => {
    if (!form.contract_id || !form.billing_period_start || !form.billing_period_end) {
      toast.error('Fill all fields'); return;
    }
    try {
      await api.post('/billing/generate', {
        contract_id: form.contract_id,
        billing_period_start: form.billing_period_start,
        billing_period_end: form.billing_period_end,
        tax_percent: Number(form.tax_percent) || 18,
      });
      toast.success('Billing advice generated');
      setShowGenerate(false);
      setForm({ contract_id: '', billing_period_start: '', billing_period_end: '', tax_percent: '18' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error generating billing'); }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/billing/${id}/approve`);
      toast.success('Billing approved');
      fetchData();
      if (selectedBilling?.id === id) {
        const updated = await api.get(`/billing/${id}`);
        setSelectedBilling(updated.data);
      }
    } catch (e) { toast.error('Approval failed'); }
  };

  const handleExport = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/billing/${id}/export/tally`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `tally_billing_${id}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported for Tally');
      fetchData();
    } catch (e) { toast.error('Export error'); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing Advice</h1>
          <p className="text-muted-foreground text-sm">Generate and export billing for Tally</p>
        </div>
        <Button onClick={() => setShowGenerate(true)} data-testid="generate-billing-btn"><Plus className="h-4 w-4 mr-2" />Generate Billing</Button>
      </div>

      <div className="grid gap-4">
        {billings.map(b => (
          <Card key={b.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setSelectedBilling(b)}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <IndianRupee className="h-4 w-4 text-primary" />
                    <span className="font-mono text-sm font-bold">{b.contract_number}</span>
                    <Badge variant="outline" className={STATUS_COLORS[b.status]}>{b.status}</Badge>
                  </div>
                  <div className="font-semibold">{b.customer_name}</div>
                  <div className="text-sm text-muted-foreground">{b.billing_period_start} to {b.billing_period_end}</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold">₹{(b.total_amount || 0).toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{(b.line_items || []).length} items &middot; {b.tax_percent}% GST</div>
                  <div className="flex gap-2 mt-2 justify-end">
                    {b.status === 'Draft' && <Button size="sm" onClick={e => { e.stopPropagation(); handleApprove(b.id); }}><CheckCircle className="h-3 w-3 mr-1" />Approve</Button>}
                    <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); handleExport(b.id); }}><Download className="h-3 w-3 mr-1" />Tally</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {billings.length === 0 && <div className="text-center py-12 text-muted-foreground">No billing advices generated yet.</div>}
      </div>

      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Billing Advice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Contract *</Label>
              <Select value={form.contract_id} onValueChange={v => setForm({ ...form, contract_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select contract" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.contract_number} — {c.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start *</Label>
                <Input type="date" value={form.billing_period_start} onChange={e => setForm({ ...form, billing_period_start: e.target.value })} />
              </div>
              <div>
                <Label>Period End *</Label>
                <Input type="date" value={form.billing_period_end} onChange={e => setForm({ ...form, billing_period_end: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>GST %</Label>
              <Input type="number" value={form.tax_percent} onChange={e => setForm({ ...form, tax_percent: e.target.value })} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowGenerate(false)}>Cancel</Button>
              <Button onClick={handleGenerate} data-testid="submit-billing-btn">Generate</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedBilling} onOpenChange={() => setSelectedBilling(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedBilling && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Billing: {selectedBilling.contract_number}
                  <Badge variant="outline" className={STATUS_COLORS[selectedBilling.status]}>{selectedBilling.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Customer:</span> <strong>{selectedBilling.customer_name}</strong></div>
                  <div><span className="text-muted-foreground">Period:</span> {selectedBilling.billing_period_start} to {selectedBilling.billing_period_end}</div>
                  <div><span className="text-muted-foreground">Generated by:</span> {selectedBilling.generated_by}</div>
                  {selectedBilling.approved_by && <div><span className="text-muted-foreground">Approved by:</span> {selectedBilling.approved_by}</div>}
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-muted/50 border-b">
                      <th className="text-left p-2">Asset</th><th className="text-left p-2">Type</th><th className="text-right p-2">Days</th><th className="text-right p-2">Rate/Day</th><th className="text-right p-2">Amount</th>
                    </tr></thead>
                    <tbody>
                      {(selectedBilling.line_items || []).map((li, i) => (
                        <tr key={i} className="border-b"><td className="p-2 font-mono text-xs">{li.asset_id}</td><td className="p-2">{li.description}</td><td className="p-2 text-right">{li.days_active}</td><td className="p-2 text-right">₹{li.daily_rate}</td><td className="p-2 text-right font-medium">₹{li.amount}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-right space-y-1 text-sm">
                  <div>Subtotal: <strong>₹{selectedBilling.subtotal}</strong></div>
                  <div>GST ({selectedBilling.tax_percent}%): <strong>₹{selectedBilling.tax_amount}</strong></div>
                  <div className="text-lg font-bold text-primary">Total: ₹{selectedBilling.total_amount}</div>
                </div>
                <div className="flex justify-end gap-2">
                  {selectedBilling.status === 'Draft' && <Button onClick={() => handleApprove(selectedBilling.id)}><CheckCircle className="h-4 w-4 mr-1" />Approve</Button>}
                  <Button variant="outline" onClick={() => handleExport(selectedBilling.id)}><Download className="h-4 w-4 mr-1" />Export for Tally</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
